import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import { calculateClt, DISMISSAL_TYPES } from './utils/cltCalculator'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const formatBRL = (value) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const CltCalculator = () => {
  useDocumentMeta({
    title: 'Calculadora de Rescisão CLT Online  jarder.dev',
    description:
      'Estime valores de rescisão trabalhista: aviso prévio, férias ' +
      'proporcionais, 13º e multa do FGTS. Grátis, apenas uma estimativa.',
  })

  const [salary, setSalary] = useState('2000')
  const [admissionDate, setAdmissionDate] = useState('')
  const [dismissalDate, setDismissalDate] = useState('')
  const [type, setType] = useState('sem_justa_causa')

  const result = useMemo(() => {
    const salaryNum = parseFloat(salary.replace(',', '.'))
    if (!salaryNum || !admissionDate || !dismissalDate) return null
    return calculateClt({
      salary: salaryNum,
      admissionDate,
      dismissalDate,
      type,
    })
  }, [salary, admissionDate, dismissalDate, type])

  const handleChange = () => trackEvent('tool_generate', { tool: 'clt_calculator', type })

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Calculadora de Rescisão CLT</h1>
      <p className="tool-disclaimer">
        <strong>Isto é apenas uma estimativa</strong>  não considera
        acordos coletivos, férias vencidas de períodos anteriores ou
        particularidades do seu contrato. Para um cálculo oficial,
        consulte o RH da empresa, um contador ou um advogado
        trabalhista.
      </p>

      <div className="tool-row">
        <div className="tool-field">
          <label htmlFor="salary">Salário bruto mensal (R$)</label>
          <input
            id="salary"
            type="text"
            inputMode="decimal"
            value={salary}
            onChange={(e) => {
              setSalary(e.target.value)
              handleChange()
            }}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="type">Tipo de desligamento</label>
          <select id="type" value={type} onChange={(e) => setType(e.target.value)}>
            {DISMISSAL_TYPES.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="tool-row">
        <div className="tool-field">
          <label htmlFor="admission">Data de admissão</label>
          <input
            id="admission"
            type="date"
            value={admissionDate}
            onChange={(e) => setAdmissionDate(e.target.value)}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="dismissal">Data de desligamento</label>
          <input
            id="dismissal"
            type="date"
            value={dismissalDate}
            onChange={(e) => setDismissalDate(e.target.value)}
          />
        </div>
      </div>

      {result && (
        <>
          <div className="tool-result-card">
            <span className="tool-result-label">Total estimado</span>
            <span className="tool-result-value">{formatBRL(result.total)}</span>
          </div>
          <ul className="tool-list">
            <li>
              <strong>Saldo de salário:</strong> {formatBRL(result.saldoSalario)}
            </li>
            {result.avisoPrevio > 0 && (
              <li>
                <strong>Aviso prévio indenizado:</strong>{' '}
                {formatBRL(result.avisoPrevio)}
              </li>
            )}
            <li>
              <strong>Férias proporcionais + 1/3:</strong>{' '}
              {formatBRL(result.feriasProporcionais)}
            </li>
            <li>
              <strong>13º salário proporcional:</strong>{' '}
              {formatBRL(result.decimoTerceiro)}
            </li>
            {result.multaFgts > 0 && (
              <li>
                <strong>Multa FGTS (estimada):</strong>{' '}
                {formatBRL(result.multaFgts)}
              </li>
            )}
          </ul>
        </>
      )}
      {(admissionDate || dismissalDate) && !result && (
        <p className="tool-error">
          Verifique as datas  o desligamento deve ser depois da admissão.
        </p>
      )}

      <div className="tool-info-section">
        <h2>O que entra na rescisão</h2>
        <p>
          <strong>Saldo de salário</strong> são os dias trabalhados no mês
          da saída. <strong>Férias proporcionais + 1/3</strong> é o
          direito a férias acumulado desde o último período aquisitivo,
          acrescido do adicional constitucional de um terço.{' '}
          <strong>13º proporcional</strong> é calculado por mês trabalhado
          no ano. Já o <strong>aviso prévio indenizado</strong> e a{' '}
          <strong>multa de 40% do FGTS</strong> só entram em demissões
          sem justa causa por parte da empresa.
        </p>
      </div>
    </div>
  )
}

export default CltCalculator
